import axios from 'axios'
import { getApiUrl } from './utils'

// Guest media vault (Cloudflare R2 direct uploads).
//
// Upload path: presign (our API) → PUT straight to the bucket via the signed
// URL (bytes never touch our server) → complete (our API verifies the object
// landed). Images are compressed client-side first, and a small preview JPEG
// (canvas downscale / video poster frame) rides along as a second PUT so the
// host gallery gets thumbnails without any server-side image pipeline.

const API = getApiUrl()
const auth = { withCredentials: true }

export interface MediaItem {
  media_id: string
  event_id: string
  uploaded_by: 'host' | 'guest'
  uploader_name: string | null
  original_filename: string | null
  content_type: string | null
  size_bytes: number
  status: 'pending' | 'ready'
  created_at: string | null
  folder: string | null
  view_url?: string
  thumb_url?: string | null
  download_url?: string
}

export interface MediaListQuery {
  media?: 'all' | 'photo' | 'video'
  uploaded_by?: 'all' | 'host' | 'guest'
  q?: string
  sort?: 'newest' | 'oldest' | 'largest' | 'uploader'
  folder?: string
  limit?: number
  offset?: number
}

export interface MediaFolder {
  name: string
  count: number
}

export interface MediaListResult {
  items: MediaItem[]
  total: number
  photo_count: number
  video_count: number
  has_more: boolean
  url_ttl: number
  folders: MediaFolder[]
  unsorted_count: number
}

export interface MediaStatus {
  provider: 'r2' | 'mock'
  guest_upload_enabled: boolean
  share_path: string
  photo_count: number
  video_count: number
  pending_count: number
  used_bytes: number
  max_items: number
  max_image_bytes: number
  max_video_bytes: number
  max_total_bytes: number
  // Paid storage state (no free tier — uploads need purchased space).
  quota_bytes: number
  service_active: boolean
  expires_at: string | null
  service_days: number
  grace_days: number
  delete_at: string | null
  /** Flat rate — storage has no tiers. */
  price_per_gb: number
  /** What the amount field is pre-filled with. */
  default_gb: number
  max_gb: number
  currency: string
}

export interface MediaPurchaseResult {
  status: 'paid' | 'pending'
  payment_id: string
  gb: number
  checkout_url: string | null
  provider?: string
}

interface PresignTarget {
  url: string
  headers: Record<string, string>
}

export interface MediaPresign {
  media_id: string
  upload: PresignTarget
  thumb?: PresignTarget
  expires_in: number
}

export interface MediaPresignRequest {
  filename: string
  content_type: string
  size_bytes: number
  uploader_name?: string
  thumb_size_bytes?: number
}

// Mirrors the backend defaults (MEDIA_MAX_* in server.py); the live values
// arrive with the public event payload / media status and take precedence.
export const MEDIA_MAX_IMAGE_BYTES = 25 * 1024 * 1024
export const MEDIA_MAX_VIDEO_BYTES = 4 * 1024 ** 3
export const MEDIA_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime'

// ---------- API calls ----------

export const getMediaStatus = async (eventId: string): Promise<MediaStatus> =>
  (await axios.get(`${API}/events/${eventId}/media/status`, auth)).data

export const updateMediaSettings = async (
  eventId: string,
  guestUploadEnabled: boolean,
): Promise<MediaStatus> =>
  (
    await axios.put(
      `${API}/events/${eventId}/media/settings`,
      { guest_upload_enabled: guestUploadEnabled },
      auth,
    )
  ).data

/** Buy storage, or top it up once purchased — same flat per-GB rate either way.
 *  Omitting `gb` falls back to the server's default_gb. Sandbox returns
 *  status:"paid"; a live gateway returns a checkout_url to open. */
export const purchaseMediaStorage = async (
  eventId: string,
  body: { gb?: number } = {},
): Promise<MediaPurchaseResult> =>
  (await axios.post(`${API}/events/${eventId}/media/purchase`, body, auth)).data

export const listEventMedia = async (
  eventId: string,
  query: MediaListQuery = {},
): Promise<MediaListResult> =>
  (await axios.get(`${API}/events/${eventId}/media`, { ...auth, params: query })).data

export const deleteEventMedia = async (eventId: string, mediaId: string) =>
  (await axios.delete(`${API}/events/${eventId}/media/${mediaId}`, auth)).data

// Reserved folder value meaning "not in any folder" (mirrors backend MEDIA_UNSORTED).
export const MEDIA_UNSORTED = '__unsorted__'

/** Mint a short-lived download link for a zip of the current view, a folder, or
 *  an explicit selection (media_ids). */
export const createArchiveLink = async (
  eventId: string,
  opts: { media?: 'all' | 'photo' | 'video'; folder?: string | null; media_ids?: string[] } = {},
): Promise<{ url: string; count: number; expires_in: number }> =>
  (
    await axios.post(
      `${API}/events/${eventId}/media/archive-link`,
      { media: opts.media ?? 'all', folder: opts.folder ?? undefined, media_ids: opts.media_ids },
      auth,
    )
  ).data

/** Assign the given items to a folder (null/'' = Unsorted). */
export const moveMedia = async (
  eventId: string,
  mediaIds: string[],
  folder: string | null,
): Promise<{ moved: number; folder: string | null }> =>
  (await axios.post(`${API}/events/${eventId}/media/move`, { media_ids: mediaIds, folder }, auth)).data

const presignHostMedia = async (eventId: string, req: MediaPresignRequest): Promise<MediaPresign> =>
  (await axios.post(`${API}/events/${eventId}/media/presign`, req, auth)).data

const completeHostMedia = async (eventId: string, mediaId: string) =>
  (await axios.post(`${API}/events/${eventId}/media/${mediaId}/complete`, {}, auth)).data

const presignGuestMedia = async (
  inviteLink: string,
  req: MediaPresignRequest,
): Promise<MediaPresign> =>
  (await axios.post(`${API}/public/event/${inviteLink}/media/presign`, req)).data

const completeGuestMedia = async (inviteLink: string, mediaId: string) =>
  (await axios.post(`${API}/public/event/${inviteLink}/media/${mediaId}/complete`, {})).data

// ---------- client-side preparation (compress / thumbnail) ----------

const IMAGE_MAX_DIM = 2560 // long-edge px for the stored copy
const IMAGE_QUALITY = 0.85
const THUMB_MAX_DIM = 480
const THUMB_QUALITY = 0.72
// Below this size + dimension, re-encoding would only burn battery.
const COMPRESS_THRESHOLD_BYTES = 4 * 1024 * 1024

export interface PreparedFile {
  blob: Blob
  contentType: string
  filename: string
  thumb: Blob | null
}

const canvasToJpeg = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))

const drawScaled = (
  source: CanvasImageSource,
  width: number,
  height: number,
  maxDim: number,
): HTMLCanvasElement => {
  const scale = Math.min(1, maxDim / Math.max(width, height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(width * scale))
  canvas.height = Math.max(1, Math.round(height * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas 2d context unavailable')
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
  return canvas
}

/** Decode an image file, honouring EXIF orientation where the browser can. */
const decodeImage = async (
  file: File,
): Promise<{ source: CanvasImageSource; width: number; height: number; cleanup: () => void }> => {
  if (typeof createImageBitmap === 'function') {
    try {
      // Older Safari throws on the options bag — retry without it.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as any).catch(
        () => createImageBitmap(file),
      )
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        cleanup: () => bitmap.close(),
      }
    } catch {
      // fall through to <img>
    }
  }
  const url = URL.createObjectURL(file)
  const img = new Image()
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('image decode failed'))
    img.src = url
  })
  return {
    source: img,
    width: img.naturalWidth,
    height: img.naturalHeight,
    cleanup: () => URL.revokeObjectURL(url),
  }
}

/**
 * Compress an image for upload (long edge ≤2560px JPEG) and build a preview
 * thumb. GIFs are passed through untouched (re-encoding would freeze the
 * animation); any decode failure falls back to the original file, thumb-less.
 */
export const prepareImage = async (file: File): Promise<PreparedFile> => {
  const passthrough: PreparedFile = {
    blob: file,
    contentType: file.type || 'image/jpeg',
    filename: file.name,
    thumb: null,
  }
  try {
    const { source, width, height, cleanup } = await decodeImage(file)
    try {
      const thumb = await canvasToJpeg(drawScaled(source, width, height, THUMB_MAX_DIM), THUMB_QUALITY)
      const isGif = file.type === 'image/gif'
      const smallEnough =
        file.size <= COMPRESS_THRESHOLD_BYTES && Math.max(width, height) <= IMAGE_MAX_DIM
      if (isGif || smallEnough) {
        return { ...passthrough, thumb }
      }
      const compressed = await canvasToJpeg(drawScaled(source, width, height, IMAGE_MAX_DIM), IMAGE_QUALITY)
      if (!compressed || compressed.size >= file.size) {
        return { ...passthrough, thumb }
      }
      return {
        blob: compressed,
        contentType: 'image/jpeg',
        filename: file.name.replace(/\.[^.]*$/, '') + '.jpg',
        thumb,
      }
    } finally {
      cleanup()
    }
  } catch {
    return passthrough
  }
}

/** Grab a poster frame from a video for the gallery thumb. Best-effort — some
 *  codecs can't be decoded in the browser; null just means no thumbnail. */
export const videoPoster = (file: File): Promise<Blob | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    let settled = false
    const done = (blob: Blob | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      URL.revokeObjectURL(url)
      video.removeAttribute('src')
      resolve(blob)
    }
    const timer = setTimeout(() => done(null), 6000)
    video.preload = 'auto'
    video.muted = true
    ;(video as any).playsInline = true
    video.onerror = () => done(null)
    video.onloadedmetadata = () => {
      video.currentTime = Math.min(0.1, (video.duration || 1) / 10)
    }
    video.onseeked = () => {
      try {
        const canvas = drawScaled(video, video.videoWidth, video.videoHeight, THUMB_MAX_DIM)
        canvas.toBlob((blob) => done(blob), 'image/jpeg', THUMB_QUALITY)
      } catch {
        done(null)
      }
    }
    video.src = url
  })

/** Compress/derive everything needed before asking for presigned URLs. */
export const prepareFile = async (file: File): Promise<PreparedFile> => {
  if (file.type.startsWith('video/')) {
    return {
      blob: file,
      contentType: file.type || 'video/mp4',
      filename: file.name,
      thumb: await videoPoster(file),
    }
  }
  return prepareImage(file)
}

// ---------- direct PUT with progress + retry ----------

export class MediaUploadError extends Error {
  status: number
  constructor(message: string, status = 0) {
    super(message)
    this.status = status
  }
}

const putWithProgress = (
  target: PresignTarget,
  body: Blob,
  onProgress?: (fraction: number) => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    // XHR instead of fetch: Safari still has no upload-progress events on fetch.
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', target.url)
    Object.entries(target.headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v))
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total)
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new MediaUploadError(`Storage upload failed (${xhr.status})`, xhr.status))
    xhr.onerror = () => reject(new MediaUploadError('Network error during upload', 0))
    xhr.ontimeout = () => reject(new MediaUploadError('Upload timed out', 0))
    xhr.send(body)
  })

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const RETRY_DELAYS_MS = [1500, 4000, 8000]

/** Errors worth another round trip on a flaky connection: network drops, 5xx,
 *  and 403 (an expired presign — the retry re-presigns fresh URLs). */
const isRetryable = (err: any): boolean => {
  if (err instanceof MediaUploadError) return err.status === 0 || err.status === 403 || err.status >= 500
  const status = err?.response?.status
  return status === undefined || status >= 500
}

export interface UploadHandlers {
  presign: (req: MediaPresignRequest) => Promise<MediaPresign>
  complete: (mediaId: string) => Promise<any>
}

export interface UploadCaps {
  maxImageBytes?: number
  maxVideoBytes?: number
}

/**
 * Full upload of one prepared file: presign → PUT (with progress) → thumb PUT
 * (best-effort) → complete. Each retry round starts from a fresh presign so
 * expired URLs self-heal; abandoned attempts are swept server-side.
 */
export const uploadPrepared = async (
  handlers: UploadHandlers,
  prepared: PreparedFile,
  uploaderName: string | undefined,
  onProgress?: (fraction: number) => void,
  caps: UploadCaps = {},
): Promise<string> => {
  const isVideo = prepared.contentType.startsWith('video/')
  const cap = isVideo
    ? caps.maxVideoBytes || MEDIA_MAX_VIDEO_BYTES
    : caps.maxImageBytes || MEDIA_MAX_IMAGE_BYTES
  if (prepared.blob.size > cap) {
    throw new MediaUploadError(`File exceeds the ${Math.round(cap / (1024 * 1024))} MB limit`, 413)
  }

  const request: MediaPresignRequest = {
    filename: prepared.filename,
    content_type: prepared.contentType,
    size_bytes: prepared.blob.size,
  }
  if (uploaderName?.trim()) request.uploader_name = uploaderName.trim()
  if (prepared.thumb) request.thumb_size_bytes = prepared.thumb.size

  let lastError: any = null
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) await wait(RETRY_DELAYS_MS[attempt - 1])
    try {
      const presign = await handlers.presign(request)
      await putWithProgress(presign.upload, prepared.blob, onProgress)
      if (prepared.thumb && presign.thumb) {
        // Preview is optional — the gallery falls back if it never lands.
        await putWithProgress(presign.thumb, prepared.thumb).catch(() => {})
      }
      await handlers.complete(presign.media_id)
      return presign.media_id
    } catch (err) {
      lastError = err
      if (!isRetryable(err)) break
    }
  }
  throw lastError
}

export const guestUploadHandlers = (inviteLink: string): UploadHandlers => ({
  presign: (req) => presignGuestMedia(inviteLink, req),
  complete: (mediaId) => completeGuestMedia(inviteLink, mediaId),
})

export const hostUploadHandlers = (eventId: string): UploadHandlers => ({
  presign: (req) => presignHostMedia(eventId, req),
  complete: (mediaId) => completeHostMedia(eventId, mediaId),
})

/** Human-readable byte size for quota/usage figures. */
export const formatBytes = (n: number): string => {
  if (!n || n <= 0) return '0 MB'
  const gb = n / 1024 ** 3
  if (gb >= 1) return `${gb >= 10 ? Math.round(gb) : gb.toFixed(1)} GB`
  const mb = n / 1024 ** 2
  if (mb >= 1) return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`
  return `${Math.max(1, Math.round(n / 1024))} KB`
}
